'use client';

import { useEffect, useRef } from 'react';
import { useTealium } from '@fshr/context/tealium-context';

type ImpressionPayload = Record<string, string | string[]>;
type BatchState = {
	queue: ImpressionPayload[];
	timer: NodeJS.Timeout | null;
};

const propertyBatch: BatchState = { queue: [], timer: null };
const ancillaryBatch: BatchState = { queue: [], timer: null };

const getBatch = (type: 'property' | 'ancillary') =>
	type === 'property' ? propertyBatch : ancillaryBatch;

export const useRecoImpressionBatchObserver = (
	payload: ImpressionPayload | null | undefined,
	type: 'property' | 'ancillary',
	enabled = true
) => {
	const ref = useRef<HTMLDivElement | null>(null);
	const hasQueued = useRef(false);
	const { trackLink } = useTealium();

	// 🔄 reset per payload
	useEffect(() => {
		hasQueued.current = false;
	}, [payload, enabled]);

	const flush = (type: 'property' | 'ancillary', trackLink: any) => {
		const batch = getBatch(type);
		if (!batch.queue.length) return;

		const merged: Record<string, string[]> = {};

		batch.queue.forEach(p => {
			Object.entries(p).forEach(([key, value]) => {
				if (!merged[key]) merged[key] = [];
				Array.isArray(value) ? merged[key].push(...value) : merged[key].push(value);
			});
		});

		trackLink('reco_impression', {
			event_name: 'reco_impression',
			...merged,
		});

		batch.queue.length = 0;
		batch.timer = null;
	};

	const scheduleBatch = (type: 'property' | 'ancillary', trackLink: any) => {
		const batch = getBatch(type);
		if (batch.timer) return;

		batch.timer = setTimeout(() => flush(type, trackLink), 500);
	};

	useEffect(() => {
		if (!enabled || !ref.current || !payload || hasQueued.current) return;

		let visibilityTimer: NodeJS.Timeout | null = null;

		const observer = new IntersectionObserver(
			entries => {
				const entry = entries[0];

				if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
					if (!visibilityTimer) {
						visibilityTimer = setTimeout(() => {
							hasQueued.current = true;

							const batch = getBatch(type);
							batch.queue.push(payload);

							scheduleBatch(type, trackLink);
							observer.disconnect();
						}, 1000);
					}
				} else if (visibilityTimer) {
					clearTimeout(visibilityTimer);
					visibilityTimer = null;
				}
			},
			{ threshold: [0, 0.5] }
		);

		observer.observe(ref.current);

		return () => {
			observer.disconnect();
			if (visibilityTimer) clearTimeout(visibilityTimer);
		};
	}, [payload, type, trackLink, enabled]);

	return ref;
};
